/**
 * UpgradePromptForm - Shows when user tries to access restricted features
 * Displays selected plan features and directs to payment
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/global/auth/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

interface UpgradePromptFormProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: string;
  requiredPlan: "fma_pro" | "fma_elite" | "ams_standard";
  appName: "FMA" | "AMS";
}

// AMS plan configuration
const AMS_PLAN = {
  name: "AMS Access",
  price: "$59/month",
  icon: Building2,
  features: [
    "Complete CRM system",
    "Unlimited contacts & clients",
    "Agent management dashboard",
    "Pipeline & deal tracking",
    "Email integration",
    "Basic reporting & analytics",
    "Mobile app access",
    "Email support",
  ],
};

export default function UpgradePromptForm({
  isOpen,
  onClose,
  currentPlan,
  requiredPlan,
  appName,
}: UpgradePromptFormProps) {
  const navigate = useNavigate();
  const { user, activeTenantId } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const Icon = AMS_PLAN.icon;

  const handleUpgrade = async () => {
    if (!user || !activeTenantId) {
      toast({
        title: "Authentication Required",
        description: "Please log in to upgrade your plan.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create a checkout session for AMS
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Get session token from Supabase
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No active session found");
      }

      // Use dedicated billing_create-checkout-session function
      const response = await fetch(
        `${supabaseUrl}/functions/v1/billing_create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            plan_code: "ams_standard",
            billing_interval: "monthly",
            organization_id: activeTenantId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      // Redirect to Stripe Checkout
      if (data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Checkout Failed",
        description:
          error instanceof Error
            ? error.message
            : "Unable to start checkout. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleContactSales = () => {
    window.location.href =
      "mailto:Info@findmyagency.net?subject=AMS%20Access%20Inquiry";
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:w-[600px] md:w-[720px] lg:w-[810px] bg-black/20 backdrop-blur-3xl border border-white/10 shadow-2xl overflow-y-auto"
      >
        <SheetHeader className="pb-6 border-b border-white/10 px-6">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-linear-to-br from-orange-400 to-orange-600 rounded-xl mb-4">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <SheetTitle className="text-2xl text-white">
            Unlock AMS Access
          </SheetTitle>
          <SheetDescription className="text-white/80 text-base">
            Your current plan ({currentPlan}) doesn't include AMS. Add{" "}
            <strong className="text-white">{AMS_PLAN.name}</strong> for powerful
            CRM tools.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6 px-6">
          {/* Pricing */}
          <div className="text-center py-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
            <div className="text-5xl font-bold text-white">
              {AMS_PLAN.price}
            </div>
            <p className="text-sm text-white/60 mt-2">
              Billed monthly • Cancel anytime
            </p>
          </div>

          {/* Features List */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">
              What's included in {AMS_PLAN.name}:
            </h3>
            <ul className="space-y-3">
              {AMS_PLAN.features.map((feature, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-white/90">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <Button
              onClick={handleUpgrade}
              disabled={isLoading}
              className="w-full bg-linear-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium"
              size="lg"
            >
              {isLoading ? "Loading..." : `Get AMS Access - ${AMS_PLAN.price}`}
            </Button>
            <Button
              onClick={handleContactSales}
              variant="outline"
              className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10"
              size="lg"
            >
              Contact Sales
            </Button>
          </div>

          {/* Footer note */}
          <div className="pt-4">
            <p className="text-xs text-center text-white/60">
              Need help choosing? Email us at{" "}
              <a
                href="mailto:Info@findmyagency.net"
                className="text-orange-400 hover:underline"
              >
                Info@findmyagency.net
              </a>
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
